import type {FunctionsSandboxActions} from './actionCreators';
import {
  CODE_BUILD_FAILED,
  CODE_BUILD_STARTED,
  CODE_BUILD_SUCCEEDED,
  FUNCTIONS_GENERATION_MISSING,
  FUNCTIONS_PREVIEW_CONNECTED,
  FUNCTIONS_PREVIEW_CONNECTING,
  FUNCTIONS_PREVIEW_DISABLED,
  FUNCTIONS_PREVIEW_FAILED,
  FUNCTIONS_PREVIEW_EMPTY,
} from './actionTypes';
import type {FunctionsPreview, FunctionsSandboxState} from './types';

export const initialFunctionsSandboxState: FunctionsSandboxState = {
  status: 'IDLE',
};

export function getFunctionsPreview(
  state: FunctionsSandboxState
): FunctionsPreview | undefined {
  return 'preview' in state ? state.preview : undefined;
}

/**
 * Preview to carry into a CONNECTING/FAILED transition for `moduleKey`. A
 * module-set change drops it — otherwise module set B's caller could invoke
 * against module set A's sandbox while B is still synchronizing.
 */
function getPreviewForModuleKey(
  state: FunctionsSandboxState,
  moduleKey: string
): FunctionsPreview | undefined {
  return 'moduleKey' in state && state.moduleKey === moduleKey
    ? getFunctionsPreview(state)
    : undefined;
}

export const FunctionsSandboxStore = (
  state: FunctionsSandboxState = initialFunctionsSandboxState,
  action: FunctionsSandboxActions
): FunctionsSandboxState => {
  const {siteFunctionsHash} = state;
  switch (action.type) {
    case FUNCTIONS_PREVIEW_CONNECTING: {
      const {moduleKey} = action.payload;
      const preview = getPreviewForModuleKey(state, moduleKey);
      const nextState: FunctionsSandboxState = preview
        ? {
            status: 'CONNECTING',
            moduleKey,
            preview,
            siteFunctionsHash,
          }
        : {
            status: 'CONNECTING',
            moduleKey,
            siteFunctionsHash,
          };
      if (
        state.status === 'CONNECTING' &&
        state.moduleKey === moduleKey &&
        state.preview === preview
      ) {
        return state;
      }
      return nextState;
    }
    case FUNCTIONS_PREVIEW_CONNECTED: {
      return {
        status: 'READY',
        moduleKey: action.payload.moduleKey,
        preview: action.payload.preview,
        siteFunctionsHash,
      };
    }
    case FUNCTIONS_PREVIEW_FAILED: {
      const preview = getPreviewForModuleKey(state, action.payload.moduleKey);
      return preview
        ? {
            status: 'FAILED',
            moduleKey: action.payload.moduleKey,
            attempts: action.payload.attempts,
            retryAt: action.payload.retryAt,
            preview,
            siteFunctionsHash,
          }
        : {
            status: 'FAILED',
            moduleKey: action.payload.moduleKey,
            attempts: action.payload.attempts,
            retryAt: action.payload.retryAt,
            siteFunctionsHash,
          };
    }
    case FUNCTIONS_PREVIEW_EMPTY: {
      const {moduleKey} = action.payload;
      if (state.status === 'NO_FUNCTIONS' && state.moduleKey === moduleKey) {
        return state;
      }
      return {
        status: 'NO_FUNCTIONS',
        moduleKey,
        siteFunctionsHash,
      };
    }
    case FUNCTIONS_PREVIEW_DISABLED: {
      if (state.status === 'DISABLED') {
        return state;
      }
      return {
        status: 'DISABLED',
        siteFunctionsHash,
      };
    }
    case FUNCTIONS_GENERATION_MISSING: {
      // A build already running predates the miss and won't re-emit the
      // evicted generation; only one that starts after it does.
      return {
        status: 'AWAITING_REBUILD',
        rebuildStarted: false,
        siteFunctionsHash,
      };
    }
    case CODE_BUILD_STARTED: {
      return state.status === 'AWAITING_REBUILD' && !state.rebuildStarted
        ? {...state, rebuildStarted: true}
        : state;
    }
    case CODE_BUILD_FAILED: {
      return state.status === 'AWAITING_REBUILD' && state.rebuildStarted
        ? {status: 'IDLE', siteFunctionsHash}
        : state;
    }
    case CODE_BUILD_SUCCEEDED: {
      const functionsHash = action.payload.functionsHash || undefined;
      if (state.status === 'AWAITING_REBUILD') {
        // The rebuild usually re-emits the same hash, so IDLE (not the hash
        // change) is what makes the engine reconnect.
        return state.rebuildStarted
          ? {status: 'IDLE', siteFunctionsHash: functionsHash}
          : state;
      }
      if (functionsHash === siteFunctionsHash) {
        return state;
      }
      return {...state, siteFunctionsHash: functionsHash};
    }
    default: {
      return state;
    }
  }
};
